import { CVData, DistincionItem, ExperienciaItem, FormacionAcademicaItem, LineaInvestigacionItem, ProyectoItem, PublicacionItem } from '../models/cv.models';
import { DatosPersonales, DireccionPerfil, datosPersonalesVacios } from '../models/perfil.models';
import { formatShortDate } from './date-format.util';
import { normalizeSearchText } from './text-format.util';

export interface CampoCurriculumVista {
  etiqueta: string;
  valor: string;
  obligatorio: boolean;
}

export interface BloqueCurriculumVista {
  titulo: string;
  registros: CampoCurriculumVista[][];
}

export function construirVistaCurriculum(
  perfil: DatosPersonales | null,
  cv: CVData
): BloqueCurriculumVista[] {
  return [
    bloquePersonal(perfil ?? datosPersonalesVacios()),
    bloqueDe('Estudios', cv.formacionAcademica.map(camposEstudio)),
    bloqueDe('Líneas de investigación', cv.lineasInvestigacion.map(camposLinea)),
    bloqueDe('Experiencia laboral', cv.experiencia.map(camposExperiencia)),
    bloqueDe('Proyectos', cv.proyectos.map(camposProyecto)),
    bloqueDe('Publicaciones y Producción', cv.publicaciones.map(camposPublicacion)),
    bloqueDe('Reconocimientos y Premios', cv.distinciones.map(camposReconocimiento)),
  ];
}

export function nombreArchivoCurriculum(perfil: DatosPersonales | null, extension: 'pdf' | 'doc'): string {
  const partes = [perfil?.primerNombre, perfil?.primerApellido].filter(Boolean).join(' ');
  const slug = normalizeSearchText(partes).replace(/\s+/g, '-') || 'usuario';
  return `curriculum-${slug}.${extension}`;
}

export function descargarCurriculumWord(
  bloques: BloqueCurriculumVista[],
  filename: string,
  perfil: DatosPersonales | null
): void {
  const html = htmlCurriculum(bloques, encabezadoCurriculum(perfil));
  descargarBlob(new Blob(['\ufeff', html], { type: 'application/msword;charset=utf-8' }), filename);
}

export function descargarCurriculumPdf(
  bloques: BloqueCurriculumVista[],
  filename: string,
  perfil: DatosPersonales | null
): void {
  const bytes = construirPdfCurriculum(bloques, encabezadoCurriculum(perfil));
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  descargarBlob(new Blob([buffer], { type: 'application/pdf' }), filename);
}

function bloquePersonal(perfil: DatosPersonales): BloqueCurriculumVista {
  const campos: CampoCurriculumVista[] = [
    campo('Primer nombre', perfil.primerNombre, true),
    campo('Segundo nombre', perfil.segundoNombre, false),
    campo('Primer apellido', perfil.primerApellido, true),
    campo('Segundo apellido', perfil.segundoApellido, false),
    campo('RUN / Pasaporte', perfil.runPasaporte, true),
    campo('Fecha de nacimiento', fechaVista(perfil.fechaNacimiento), false),
    campo('Nacionalidad', perfil.nacionalidad, false),
    campo('Género registral', perfil.generoRegistral || perfil.sexoRegistral, false),
    campo('Nombre social', perfil.nombreSocialCompleto, false),
    campo('Correo principal', perfil.correoPrincipal, true),
    campo('Correo alternativo', perfil.correoAlternativo, false),
    campo('Teléfono', perfil.telefono, true),
    ...perfil.direcciones.flatMap((direccion, index) => camposDireccion(direccion, perfil.direcciones.length > 1 ? index + 1 : 0)),
    campo('Institución actual', perfil.institucionActual, false),
    campo('Cargo / rol', perfil.cargoRol, false),
    campo('Departamento / unidad', perfil.departamentoUnidad, false),
    campo('Tipo de vínculo', perfil.tipoVinculo, false),
    campo('LinkedIn académico', perfil.linkedinAcademico, false),
  ].filter((item): item is CampoCurriculumVista => item !== null);

  return { titulo: 'Información personal', registros: campos.length ? [campos] : [] };
}

function camposDireccion(direccion: DireccionPerfil, numero: number): Array<CampoCurriculumVista | null> {
  const sufijo = numero ? ` (${numero})` : '';
  return [
    campo(`Tipo de dirección${sufijo}`, direccion.tipo, false),
    campo(`País de residencia${sufijo}`, direccion.paisResidencia, false),
    campo(`Región${sufijo}`, direccion.region, false),
    campo(`Comuna${sufijo}`, direccion.comuna, false),
    campo(`Calle / número / depto${sufijo}`, direccion.calleNumeroDepto, false),
    campo(`Referencia adicional${sufijo}`, direccion.referenciaAdicional, false),
  ];
}

function camposEstudio(item: FormacionAcademicaItem): CampoCurriculumVista[] {
  return filtrarCampos([
    campo('Tipo de antecedente académico', item.tipoAntecedente, true),
    campo('Título / Grado / Especialización', item.grado, true),
    campo('Institución', item.institucion, true),
    campo('País', item.pais, true),
    campo('Estado del estudio', etiquetaEstado(item.estado), true),
    campo('Fecha de inicio', fechaVista(item.fechaInicio, item.anioInicio), true),
    campo('Fecha de término', fechaVista(item.fechaTermino, item.anioTermino), false),
  ]);
}

function camposLinea(item: LineaInvestigacionItem): CampoCurriculumVista[] {
  return filtrarCampos([
    campo('Línea de investigación', item.linea, true),
    campo('Disciplina / Área', item.disciplinaArea, true),
  ]);
}

function camposExperiencia(item: ExperienciaItem): CampoCurriculumVista[] {
  return filtrarCampos([
    campo('Institución / Organización', item.institucion, true),
    campo('Cargo / Posición', item.cargo, true),
    campo('Experiencia vigente', item.experienciaVigente, true),
    campo('Fecha de inicio', fechaVista(item.fechaInicio), true),
    campo('Fecha de término', fechaVista(item.fechaTermino), false),
    campo('Descripción / Funciones', item.funcionesPrincipales, false),
  ]);
}

function camposProyecto(item: ProyectoItem): CampoCurriculumVista[] {
  return filtrarCampos([
    campo('Nombre del proyecto', item.nombreProyecto, true),
    campo('Código / Folio', item.codigoFolio, false),
    campo('Fuente de financiamiento', item.fuenteFinanciamiento, true),
    campo('Rol / Participación', item.rol, true),
    campo('Fecha de inicio', fechaVista(item.fechaInicio, item.anioInicio), true),
    campo('Fecha de término', fechaVista(item.fechaTermino, item.anioTermino), false),
  ]);
}

function camposPublicacion(item: PublicacionItem): CampoCurriculumVista[] {
  return filtrarCampos([
    campo('Tipo de publicación / producción', item.tipo, true),
    campo('Título', item.titulo, true),
    campo('Estructura de Autores', item.estructuraAutores, true),
    campo('Revista / Editorial / Evento', item.revistaEditorial, false),
    campo('DOI / Enlace', item.doiEnlace, true),
    campo('Año / Fecha', fechaVista(item.fecha, item.anio), true),
  ]);
}

function camposReconocimiento(item: DistincionItem): CampoCurriculumVista[] {
  return filtrarCampos([
    campo('Nombre del reconocimiento / premio', item.nombreReconocimiento, true),
    campo('Institución otorgante', item.institucionOtorga, true),
    campo('Enlace de respaldo', item.enlaceRespaldo, false),
    campo('Año', fechaVista(item.fecha, item.anio), true),
    campo('Descripción / Funciones', item.descripcion, false),
  ]);
}

function bloqueDe(titulo: string, registros: CampoCurriculumVista[][]): BloqueCurriculumVista {
  return { titulo, registros: registros.filter((registro) => registro.length > 0) };
}

function campo(etiqueta: string, valor: string, obligatorio: boolean): CampoCurriculumVista | null {
  const texto = (valor ?? '').trim();
  if (!texto) return null;
  return { etiqueta, valor: texto, obligatorio };
}

function filtrarCampos(campos: Array<CampoCurriculumVista | null>): CampoCurriculumVista[] {
  return campos.filter((item): item is CampoCurriculumVista => item !== null);
}

function etiquetaEstado(estado: string): string {
  if (estado === 'en_curso') return 'En curso';
  if (estado === 'finalizado') return 'Finalizado';
  return estado;
}

function fechaVista(fecha: string, anio = ''): string {
  const texto = (fecha ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) return formatShortDate(texto, texto);
  if (texto) return texto;
  return (anio ?? '').trim();
}

interface EncabezadoCurriculum {
  nombre: string;
  resumen: string;
  contacto: string;
}

function encabezadoCurriculum(perfil: DatosPersonales | null): EncabezadoCurriculum {
  const nombre =
    [perfil?.primerNombre, perfil?.segundoNombre, perfil?.primerApellido, perfil?.segundoApellido]
      .filter(Boolean)
      .join(' ') || 'Mi currículum';
  const resumen = [perfil?.cargoRol, perfil?.institucionActual].filter(Boolean).join(' · ');
  const lugar = [
    perfil?.ciudad || perfil?.direcciones?.[0]?.comuna,
    perfil?.pais || perfil?.direcciones?.[0]?.paisResidencia,
  ]
    .filter(Boolean)
    .join(', ');
  const contacto = [perfil?.runPasaporte, perfil?.correoPrincipal, perfil?.telefono, lugar]
    .filter(Boolean)
    .join('  ·  ');
  return { nombre, resumen, contacto };
}

function bloquesConDatos(bloques: BloqueCurriculumVista[]): BloqueCurriculumVista[] {
  return bloques.filter((bloque) => bloque.registros.length > 0);
}

function htmlCurriculum(bloques: BloqueCurriculumVista[], encabezado: EncabezadoCurriculum): string {
  const secciones = bloquesConDatos(bloques)
    .map((bloque) => {
      const registros = bloque.registros
        .map((registro, index) => {
          const filas = registro
            .map(
              (campoItem) =>
                `<tr>
                  <td width="220" valign="top" style="width:220px;padding:4px 16px 4px 0;color:#5c6570;font-size:11pt;">${escaparHtml(campoItem.etiqueta)}</td>
                  <td valign="top" style="padding:4px 0;color:#1a1a1a;font-size:11pt;">${escaparHtml(campoItem.valor)}</td>
                </tr>`
            )
            .join('');
          const separador =
            index > 0
              ? `<tr><td colspan="2" style="border-top:1px solid #e4e7eb;height:8px;font-size:4pt;line-height:8px;">&nbsp;</td></tr>`
              : '';
          return `${separador}${filas}`;
        })
        .join('');
      return `
        <table width="100%" cellpadding="0" cellspacing="0" style="width:100%;border-collapse:collapse;margin:0 0 6px 0;">
          <tr>
            <td style="font-size:13pt;font-weight:bold;color:#0f60b6;border-bottom:1.5pt solid #0f60b6;padding:18px 0 6px 0;">${escaparHtml(bloque.titulo)}</td>
          </tr>
        </table>
        <table width="100%" cellpadding="0" cellspacing="0" style="width:100%;border-collapse:collapse;margin:0 0 4px 0;">
          ${registros}
        </table>`;
    })
    .join('');

  const resumen = encabezado.resumen
    ? `<p style="margin:4px 0 0 0;font-size:12pt;color:#4b5563;">${escaparHtml(encabezado.resumen)}</p>`
    : '';
  const contacto = encabezado.contacto
    ? `<p style="margin:8px 0 0 0;font-size:10.5pt;color:#4b5563;">${escaparHtml(encabezado.contacto)}</p>`
    : '';

  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" lang="es">
<head>
<meta charset="utf-8" />
<meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
<title>${escaparHtml(encabezado.nombre)}</title>
<!--[if gte mso 9]><xml>
<w:WordDocument>
<w:View>Print</w:View>
<w:Zoom>100</w:Zoom>
<w:DoNotOptimizeForBrowser/>
</w:WordDocument>
</xml><![endif]-->
<style>
  @page { size: A4; margin: 2cm; }
  body { font-family: Calibri, Arial, Helvetica, sans-serif; color: #1a1a1a; }
</style>
</head>
<body>
  <table width="100%" cellpadding="0" cellspacing="0" style="width:100%;border-collapse:collapse;border-bottom:2pt solid #0f60b6;margin-bottom:8px;">
    <tr>
      <td style="padding:0 0 12px 0;">
        <p style="margin:0;font-size:22pt;font-weight:bold;color:#12345a;">${escaparHtml(encabezado.nombre)}</p>
        ${resumen}
        ${contacto}
      </td>
    </tr>
  </table>
  ${secciones || '<p style="color:#6b7280;">Sin información para exportar.</p>'}
</body>
</html>`;
}

function escaparHtml(valor: string): string {
  return valor
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function descargarBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = filename;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  URL.revokeObjectURL(url);
}

function construirPdfCurriculum(bloques: BloqueCurriculumVista[], encabezado: EncabezadoCurriculum): Uint8Array {
  const paginas = paginarPdf(bloquesConDatos(bloques), encabezado);
  const objetos: string[] = [];
  objetos[1] = '<< /Type /Catalog /Pages 2 0 R >>';
  const kids = paginas.map((_, index) => `${3 + index * 2} 0 R`).join(' ');
  objetos[2] = `<< /Type /Pages /Kids [${kids}] /Count ${paginas.length} >>`;

  paginas.forEach((contenidoPagina, index) => {
    const idPagina = 3 + index * 2;
    const idContenido = idPagina + 1;
    const stream = streamPagina(contenidoPagina);
    objetos[idPagina] =
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Contents ${idContenido} 0 R /Resources << /Font << /F1 ${3 + paginas.length * 2} 0 R /F2 ${4 + paginas.length * 2} 0 R >> >> >>`;
    objetos[idContenido] = `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`;
  });

  const idFuente = 3 + paginas.length * 2;
  objetos[idFuente] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>';
  objetos[idFuente + 1] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>';

  return ensamblarPdf(objetos);
}

type PdfOp =
  | { tipo: 'nombre'; texto: string }
  | { tipo: 'meta'; texto: string }
  | { tipo: 'seccion'; texto: string }
  | { tipo: 'campo'; etiqueta: string; valor: string[] }
  | { tipo: 'regla' }
  | { tipo: 'separador' };

const PDF_MARGEN = 50;
const PDF_ANCHO = 545;
const PDF_ETIQUETA_X = 50;
const PDF_VALOR_X = 230;
const PDF_Y_INICIO = 790;
const PDF_Y_MIN = 56;

function paginarPdf(bloques: BloqueCurriculumVista[], encabezado: EncabezadoCurriculum): PdfOp[][] {
  const ops: PdfOp[] = [{ tipo: 'nombre', texto: encabezado.nombre }];
  if (encabezado.resumen) ops.push({ tipo: 'meta', texto: encabezado.resumen });
  if (encabezado.contacto) {
    wrapPdf(encabezado.contacto, 92).forEach((texto) => ops.push({ tipo: 'meta', texto }));
  }
  ops.push({ tipo: 'regla' });

  bloques.forEach((bloque) => {
    ops.push({ tipo: 'seccion', texto: bloque.titulo });
    bloque.registros.forEach((registro, index) => {
      if (index > 0) ops.push({ tipo: 'separador' });
      registro.forEach((campoItem) => {
        ops.push({ tipo: 'campo', etiqueta: campoItem.etiqueta, valor: wrapPdf(campoItem.valor, 58) });
      });
    });
  });

  if (!bloques.length) {
    ops.push({ tipo: 'meta', texto: 'Sin información para exportar.' });
  }

  const paginas: PdfOp[][] = [];
  let actual: PdfOp[] = [];
  let y = PDF_Y_INICIO;

  const flush = (): void => {
    if (actual.length) paginas.push(actual);
    actual = [];
    y = PDF_Y_INICIO;
  };

  ops.forEach((op) => {
    const alto = altoOp(op);
    if (y - alto < PDF_Y_MIN) flush();
    actual.push(op);
    y -= alto;
  });
  flush();
  return paginas.length ? paginas : [[{ tipo: 'nombre', texto: encabezado.nombre }]];
}

function altoOp(op: PdfOp): number {
  if (op.tipo === 'nombre') return 28;
  if (op.tipo === 'meta') return 16;
  if (op.tipo === 'seccion') return 32;
  if (op.tipo === 'regla') return 14;
  if (op.tipo === 'separador') return 12;
  return Math.max(14, op.valor.length * 13);
}

function streamPagina(ops: PdfOp[]): string {
  const partes: string[] = [];
  let y = PDF_Y_INICIO;

  ops.forEach((op) => {
    if (op.tipo === 'nombre') {
      partes.push('BT', '/F2 18 Tf', `0.07 0.20 0.35 rg`, `${PDF_MARGEN} ${y} Td`, `(${escaparPdf(op.texto)}) Tj`, 'ET');
      y -= 28;
      return;
    }
    if (op.tipo === 'meta') {
      partes.push('BT', '/F1 10 Tf', `0.35 0.40 0.45 rg`, `${PDF_MARGEN} ${y} Td`, `(${escaparPdf(op.texto)}) Tj`, 'ET');
      y -= 16;
      return;
    }
    if (op.tipo === 'regla') {
      partes.push('0.06 0.38 0.71 RG', '1.4 w', `${PDF_MARGEN} ${y} m ${PDF_ANCHO} ${y} l S`);
      y -= 14;
      return;
    }
    if (op.tipo === 'seccion') {
      y -= 8;
      partes.push('BT', '/F2 12 Tf', `0.06 0.38 0.71 rg`, `${PDF_MARGEN} ${y} Td`, `(${escaparPdf(op.texto)}) Tj`, 'ET');
      y -= 8;
      partes.push('0.77 0.83 0.91 RG', '0.8 w', `${PDF_MARGEN} ${y} m ${PDF_ANCHO} ${y} l S`);
      y -= 16;
      return;
    }
    if (op.tipo === 'separador') {
      partes.push('0.90 0.91 0.93 RG', '0.5 w', `${PDF_MARGEN} ${y} m ${PDF_ANCHO} ${y} l S`);
      y -= 12;
      return;
    }

    partes.push(
      'BT',
      '/F1 10 Tf',
      '0.36 0.40 0.44 rg',
      `${PDF_ETIQUETA_X} ${y} Td`,
      `(${escaparPdf(truncarPdf(op.etiqueta, 34))}) Tj`,
      'ET'
    );
    op.valor.forEach((linea, index) => {
      const yValor = y - index * 13;
      partes.push('BT', '/F1 10 Tf', '0.10 0.10 0.10 rg', `${PDF_VALOR_X} ${yValor} Td`, `(${escaparPdf(linea)}) Tj`, 'ET');
    });
    y -= Math.max(14, op.valor.length * 13);
  });

  return partes.join('\n');
}

function truncarPdf(texto: string, max: number): string {
  if (texto.length <= max) return texto;
  return `${texto.slice(0, max - 3)}...`;
}

function wrapPdf(texto: string, max: number): string[] {
  const palabras = texto.split(/\s+/);
  const lineas: string[] = [];
  let actual = '';
  palabras.forEach((palabra) => {
    const piezas = palabra.length > max ? partirPalabra(palabra, max) : [palabra];
    piezas.forEach((pieza) => {
      const candidato = actual ? `${actual} ${pieza}` : pieza;
      if (candidato.length > max && actual) {
        lineas.push(actual);
        actual = pieza;
      } else {
        actual = candidato;
      }
    });
  });
  if (actual) lineas.push(actual);
  return lineas.length ? lineas : [''];
}

function partirPalabra(texto: string, max: number): string[] {
  const partes: string[] = [];
  for (let i = 0; i < texto.length; i += max) {
    partes.push(texto.slice(i, i + max));
  }
  return partes;
}

function escaparPdf(texto: string): string {
  let salida = '';
  for (const caracter of texto) {
    if (caracter === '\\' || caracter === '(' || caracter === ')') {
      salida += `\\${caracter}`;
      continue;
    }
    const codigo = caracter.charCodeAt(0);
    if (codigo === 10 || codigo === 13) {
      salida += ' ';
    } else if (codigo < 128) {
      salida += caracter;
    } else if (codigo <= 255) {
      salida += `\\${codigo.toString(8).padStart(3, '0')}`;
    } else {
      salida += '?';
    }
  }
  return salida;
}

function ensamblarPdf(objetos: string[]): Uint8Array {
  const partes: string[] = ['%PDF-1.4\n'];
  const offsets = [0];
  for (let i = 1; i < objetos.length; i++) {
    offsets[i] = partes.reduce((total, parte) => total + parte.length, 0);
    partes.push(`${i} 0 obj\n${objetos[i]}\nendobj\n`);
  }

  const xrefPos = partes.reduce((total, parte) => total + parte.length, 0);
  const xref = [`xref\n0 ${objetos.length}\n0000000000 65535 f \n`];
  for (let i = 1; i < objetos.length; i++) {
    xref.push(`${String(offsets[i]).padStart(10, '0')} 00000 n \n`);
  }
  partes.push(...xref);
  partes.push(`trailer\n<< /Size ${objetos.length} /Root 1 0 R >>\nstartxref\n${xrefPos}\n%%EOF\n`);

  const pdf = partes.join('');
  const bytes = new Uint8Array(pdf.length);
  for (let i = 0; i < pdf.length; i++) {
    bytes[i] = pdf.charCodeAt(i) & 0xff;
  }
  return bytes;
}
